<template>
  <div
    :class="classes"
  >
    <div
      v-show="shouldShowLandscapeWarning"
      class="landscape-warning"
    >
      <div
        class="landscape-warning__message-wrapper"
      >
        {{ $t('landscapeWarning') }}
      </div>
    </div>
    <modals-container />
    <suspense>
      <AppDrawer />
    </suspense>
    <div class="AppManager">
      <Manager />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Channel } from 'channelx';
import { throttle } from 'lodash-es';
import { v4 as uuidV4 } from 'uuid';
import { isIOS, isAndroid } from 'mobile-device-detect';
import { useStore } from 'vuex';
import { computed, defineAsyncComponent, nextTick, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { Channels } from '@/app.manager/events-manager/Channels';
import { IEvent } from '@/app.manager/events-manager/events/IEvent';
import { AccessEvent } from '@/app.manager/events-manager/events/AccessEvent';
import { authService } from '@/shared/services/authService';
import {
  isSmallDevice,
  isExtraSmallDevice,
  isMobile,
  DESKTOP_VIEW_WIDTH,
  useWorkflowManager,
} from '@/shared/helpers/helpers';

const AppDrawer = defineAsyncComponent(() => import('@/components/AppDrawer/AppDrawer.vue'));
const Manager = defineAsyncComponent(() => import('@/app.manager/events-manager/Manager.vue'));

const desktopView = ref(false);
const isSmallMobile = ref(false);
const isExtraSmallMobile = ref(false);
const videoStatKey = ref(undefined);
const orientation = ref('portrait');
const store = useStore();
const route = useRoute();
const direction = computed(() => store.getters.direction);
const supportsLandscapeWarning = computed(() => store.getters.supportsLandscapeWarning);
const shouldShowLandscapeWarning = computed(() => supportsLandscapeWarning.value
  && orientation.value === 'landscape'
  && isMobile()
  && !useWorkflowManager()?.pageUiConfigs?.ui?.ignoreLandscapeWarning);

const classes = computed(() => [
  'App',
  {
    'desktop-view': desktopView.value,
    'device-mobile': isMobile(),
    'device-ios': isIOS,
    'device-android': isAndroid,
    'small-mobile-device': isSmallMobile.value,
    'x-small-mobile-device': isExtraSmallMobile.value,
    'show-landscape-warning': shouldShowLandscapeWarning.value,
  },
  direction.value,
  orientation.value,
  route.matched.map(m => `route-${String(m.name)}`),
]);

function loggedIn() {
  const event: IEvent = { eventName: AccessEvent.LOGGED_IN };

  Channel.use(Channels.eventsChannel).publish(event);
}

function anonymousInit() {
  const event: IEvent = { eventName: AccessEvent.ANONYMOUS_INIT };

  Channel.use(Channels.eventsChannel).publish(event);
}

function updateVars() {
  const vh = window.innerHeight * 0.01;

  document.documentElement.style.setProperty('--vh', `${vh}px`);
}

function handleResize() {
  desktopView.value = document.documentElement.clientWidth >= DESKTOP_VIEW_WIDTH;
  isExtraSmallMobile.value = isExtraSmallDevice();
  isSmallMobile.value = isSmallDevice();

  updateVars();
}

function handleScreenOrientationChange() {
  let orientationType = window.screen?.orientation?.type as string;

  if (!orientationType) {
    orientationType = [90, 270].includes(window.orientation) ? 'landscape' : 'portrait';
  }

  if (orientationType.includes('landscape')) {
    orientation.value = 'landscape';
  } else {
    orientation.value = 'portrait';
  }
}

onMounted(async () => {
  await store.dispatch('httpModule/setIsLoading', true);
  const isLoggedIn = authService.isLoggedIn();

  videoStatKey.value = uuidV4();
  if (isLoggedIn) {
    loggedIn();
  } else {
    await anonymousInit();
  }
});

nextTick(() => {
  const throttledResizeHandler = throttle(handleResize, 150);

  window.addEventListener('resize', throttledResizeHandler);

  if (window.screen?.orientation) {
    window.screen.orientation.addEventListener('change', handleScreenOrientationChange);
  } else {
    window.addEventListener('orientationchange', handleScreenOrientationChange);
  }

  handleResize();
  handleScreenOrientationChange();
});
</script>

<style lang="less">
@import "./shared/styles/style.less";

body,
html {
  padding: 0;
  margin:  0;
  height:  100%;
}

body {
  // Disable scrolling of the entire page, as the header shouldn't be scrolled
  overflow:                hidden;
  font-family:             Poppins, Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing:  antialiased;
  -moz-osx-font-smoothing: grayscale;
  display:                 flex;

  &.font-rtl {
    font-family: "Open Sans Hebrew", sans-serif;
  }
}

.rtl {
  direction:   rtl;
  font-family: "Open Sans Hebrew", sans-serif;

  input[type=date]::-webkit-calendar-picker-indicator {
    position: absolute;
    left:     -20px;
  }
}

.show-landscape-warning.device-mobile {
  .AppDrawer::before {
    content: '';
    position: absolute;
    top:      0;
    left:     0;
    width:    100%;
    height:   100%;
    z-index:  1000;
    background: rgba(70, 70, 70, 0.41);
  }
}

.invalid-orientation {
  position:    absolute;
  left:        0;
  right:       0;
  bottom:      0;
  top:         0;
  display:     flex;
  font-size:   34px;
  align-items: center;
  text-align:  center;
  font-weight: bold;
  color:       var(--primary--mario);
}

#app, .App {
  height:    100%;
  width:     100%;
  direction: var(--direction);
}

@media screen and (min-width: @Desktop-Breakpoint) {
  #app, .App {
    display:         flex;
    width:           100%;
    justify-content: center;
  }
}

@media screen and (orientation: landscape) {
  .landscape-warning {
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;

    &__message-wrapper {
      background:    white;
      padding:       45px 20px;
      z-index:       1001;
      box-shadow:    0 0 2px 0 #0000004D;
      font-size:     18px;
      font-weight:   400;
      line-height:   30px;
      text-align:    center;
      border:        1px solid #DDDDDD;
      border-radius: 12px;
      width:         345px;
    }
  }
  .App.landscape-warning.device-mobile .AppDrawer-Content {
    overflow: hidden;
  }
}
</style>
